// Coaching Contract panel — used by:
//   1. The OnboardingFlow (step 2, first sign-in, gated)
//   2. The versioned re-accept modal (when contract.version bumps)
//   3. The always-accessible read-only re-read link (G.2, V1.x — read mode)
//
// Renders the contract text + an acceptance checkbox. Acceptance is required
// to proceed (gate disabled until checked). The "What changed" banner is
// shown when `mode === 'reAccept'` per G.5.

import { useEffect, useState } from 'react';
import { Alert, Button } from '@gc-coach/design-system';
import type { GetCoachingContractResponse } from '@gc-coach/shared';
import { acceptCoachingContract, getCoachingContract } from '../api/endpoints.js';
import { useI18n } from '../i18n/index.js';
import styles from './CoachingContractView.module.css';

export interface CoachingContractViewProps {
  /** First-time onboarding step OR a forced re-accept after a version bump. */
  mode: 'onboarding' | 'reAccept' | 'readonly';
  /** Called after the user accepts (and the POST returns OK). */
  onAccepted: () => void;
  /** Called when the user dismisses (read-only mode only). */
  onClose?: () => void;
}

export function CoachingContractView({
  mode,
  onAccepted,
  onClose,
}: CoachingContractViewProps) {
  const { t } = useI18n();
  const [contract, setContract] = useState<GetCoachingContractResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [accepted, setAccepted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const result = await getCoachingContract();
      if (cancelled) return;
      if (result.ok) {
        setContract(result.data);
      } else {
        setError(t('onboarding.contract.loadError'));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [t]);

  async function onAcceptClick() {
    if (!contract || !accepted || submitting) return;
    setSubmitting(true);
    setError(null);
    const result = await acceptCoachingContract({ version: contract.contract.version });
    setSubmitting(false);
    if (result.ok) {
      onAccepted();
    } else {
      setError(t('onboarding.contract.acceptError'));
    }
  }

  const isReadonly = mode === 'readonly';
  const isReAccept = mode === 'reAccept';

  return (
    <div className={styles.panel}>
      {/* Header */}
      <header className={styles.header}>
        {isReAccept ? (
          <>
            <span className={styles.eyebrowWarn}>{t('onboarding.contract.reAcceptEyebrow')}</span>
            <h2 className={styles.title}>{t('onboarding.contract.reAcceptHeadline')}</h2>
          </>
        ) : (
          <>
            <span className={styles.eyebrow}>{t('onboarding.contract.eyebrow')}</span>
            <h2 className={styles.title}>{t('onboarding.contract.headline')}</h2>
          </>
        )}
      </header>

      {/* What-changed banner (reAccept only) */}
      {isReAccept && contract?.contract.changeSummary && (
        <div className={styles.changeBanner}>
          {t('onboarding.contract.whatChangedLabel')} —{' '}
          v{contract.contract.version}: {contract.contract.changeSummary}
        </div>
      )}

      {/* Contract body */}
      <div className={styles.contractBody}>
        {error && (
          <div className={styles.alert}>
            <Alert severity="danger">{error}</Alert>
          </div>
        )}
        {!contract && !error && (
          <p className={styles.loading}>{t('onboarding.contract.loading')}</p>
        )}
        {contract && (
          <div className={styles.contractText}>
            <ContractMarkdown text={contract.contract.text} />
          </div>
        )}
      </div>

      {/* Acceptance checkbox + CTA */}
      {!isReadonly && (
        <footer className={styles.footer}>
          <label className={styles.checkboxRow}>
            <input
              type="checkbox"
              checked={accepted}
              onChange={(e) => setAccepted(e.target.checked)}
              className={styles.checkbox}
              disabled={!contract || submitting}
            />
            <span className={styles.checkboxLabel}>
              {isReAccept
                ? t('onboarding.contract.reAcceptCheckboxLabel')
                : t('onboarding.contract.checkboxLabel')}
            </span>
          </label>

          <div className={styles.footerActions}>
            <span className={styles.requiredHint}>
              {t(
                isReAccept
                  ? 'onboarding.contract.requiredToContinue'
                  : 'onboarding.contract.requiredHint',
              )}
            </span>
            <Button
              kind="primary"
              onClick={() => void onAcceptClick()}
              disabled={!contract || !accepted || submitting}
            >
              {t(
                isReAccept
                  ? 'onboarding.contract.reAcceptCta'
                  : 'onboarding.contract.acceptCta',
              )}
            </Button>
          </div>
        </footer>
      )}

      {isReadonly && onClose && (
        <footer className={styles.footer}>
          <div className={styles.footerActions}>
            <Button kind="ghost" onClick={onClose}>
              {t('common.close')}
            </Button>
          </div>
        </footer>
      )}
    </div>
  );
}

// ─── Markdown rendering (minimal) ─────────────────────────────────────────
// Contract text uses simple markdown — `## headings`, paragraphs, _italic_.
// Not running a full markdown lib for a single surface; this handles the
// patterns the seed contract + future Olivier text will use.

function ContractMarkdown({ text }: { text: string }) {
  const blocks = text.split(/\n\n+/);
  return (
    <>
      {blocks.map((block, i) => {
        const trimmed = block.trim();
        if (!trimmed) return null;
        if (trimmed.startsWith('## ')) {
          return <h3 key={i} className={styles.h3}>{trimmed.slice(3)}</h3>;
        }
        // _italic_ → muted line
        if (trimmed.startsWith('_') && trimmed.endsWith('_')) {
          return <p key={i} className={styles.muted}>{trimmed.slice(1, -1)}</p>;
        }
        return <p key={i} className={styles.p}>{trimmed}</p>;
      })}
    </>
  );
}
